import { AlertTriangle, CheckCircle2, ShieldCheck, Stethoscope, Wrench } from 'lucide-react';
import { useState } from 'react';
import { PERMISSION } from '@obraplan/core/domain/access/permissions';
import {
  groupFindings,
  isRepairable,
  INTEGRITY_ISSUE,
  type IntegrityFinding,
  type IntegrityReport,
} from '@obraplan/core/domain/integrity/integrity-check';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/dialog';
import { EmptyState } from '@/components/feedback/states';
import { useContainer, usePermission, useSession } from '@/features/session/use-session';
import { useCommand } from '@/lib/use-command';

/**
 * Verificação de integridade referencial — hoje, uma coisa só: a chave estrangeira prova
 * que a linha existe, nunca que ela está ativa, e o soft delete cabe nessa brecha. O resto
 * do catálogo virou restrição do banco e saiu daqui. Não roda ao abrir, e nenhum reparo
 * acontece sem confirmação nem adivinha o que exige decisão humana.
 */
export function IntegrityPage() {
  const container = useContainer();
  const session = useSession();
  const canRepair = usePermission(PERMISSION.SYSTEM_INTEGRITY);

  const [report, setReport] = useState<IntegrityReport | undefined>(undefined);
  const [pendingRepair, setPendingRepair] = useState<readonly IntegrityFinding[] | undefined>(
    undefined,
  );

  const [runCheck, checkState] = useCommand(() => container.runIntegrityCheck.execute(session), {
    onSuccess: (value) => setReport(value),
  });

  const [runRepair] = useCommand(
    (findings: readonly IntegrityFinding[]) => container.repairIntegrity.execute(session, findings),
    {
      successMessage: (summary) =>
        summary.repaired === 1
          ? '1 problema corrigido.'
          : String(summary.repaired) + ' problemas corrigidos.',
      // Reverifica sempre: o reparo pode revelar o próximo problema.
      onSuccess: () => void runCheck(),
    },
  );

  const groups = report === undefined ? [] : groupFindings(report.findings);
  const repairableAll = report === undefined ? [] : report.findings.filter(isRepairable);

  const confirmRepair = () => {
    const findings = pendingRepair;
    setPendingRepair(undefined);
    if (findings !== undefined) void runRepair(findings);
  };

  return (
    <div className="flex flex-col gap-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-2xl font-semibold">Integridade dos dados</h1>
        <p className="text-(--color-content-muted)">
          Procura registros que apontam para outros já excluídos.
        </p>
      </header>

      <Card>
        <CardContent className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex gap-3">
            <ShieldCheck
              className="mt-0.5 size-5 shrink-0 text-(--color-content-muted)"
              aria-hidden="true"
            />
            <p className="text-sm text-(--color-content-muted)">
              O banco garante que a frente existe, não que ela ainda está ativa — e é essa a brecha
              que a varredura cobre. Rode depois de uma correção manual, não sempre.
            </p>
          </div>
          <Button
            variant="primary"
            className="shrink-0"
            onClick={() => void runCheck()}
            disabled={checkState.isPending}
          >
            <Stethoscope aria-hidden="true" />
            {checkState.isPending ? 'Verificando…' : 'Verificar agora'}
          </Button>
        </CardContent>
      </Card>

      {report !== undefined && report.findings.length === 0 && (
        <EmptyState
          icon={<CheckCircle2 aria-hidden="true" />}
          title="Nenhum problema encontrado"
          description={
            report.checkedRecords === 0
              ? 'Não há registros para verificar neste dispositivo.'
              : report.checkedRecords + ' registros verificados, todos consistentes.'
          }
        />
      )}

      {report !== undefined && report.findings.length > 0 && (
        <>
          {/* Só avisos não é vermelho: binário órfão ocupa espaço, não quebra a obra. */}
          <Card
            className={
              report.errorCount > 0
                ? 'border-(--color-state-danger)'
                : 'border-(--color-state-warn)'
            }
          >
            <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <AlertTriangle
                  className={
                    report.errorCount > 0
                      ? 'size-5 shrink-0 text-(--color-state-danger)'
                      : 'size-5 shrink-0 text-(--color-state-warn)'
                  }
                  aria-hidden="true"
                />
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  {report.errorCount > 0 && (
                    <Badge tone="danger" icon={<AlertTriangle aria-hidden="true" />}>
                      {report.errorCount} {report.errorCount === 1 ? 'erro' : 'erros'}
                    </Badge>
                  )}
                  {report.warningCount > 0 && (
                    <Badge tone="warn" icon={<AlertTriangle aria-hidden="true" />}>
                      {report.warningCount} {report.warningCount === 1 ? 'aviso' : 'avisos'}
                    </Badge>
                  )}
                  <span className="text-(--color-content-muted)">
                    em {report.checkedRecords} registros verificados
                  </span>
                </div>
              </div>
              {canRepair && repairableAll.length > 0 && (
                <Button
                  variant={report.errorCount > 0 ? 'danger' : 'secondary'}
                  className="shrink-0"
                  onClick={() => setPendingRepair(repairableAll)}
                >
                  <Wrench aria-hidden="true" />
                  Corrigir automaticamente ({repairableAll.length})
                </Button>
              )}
            </CardContent>
          </Card>

          {groups.map((group) => {
            const descriptor = INTEGRITY_ISSUE[group.kind];
            const automatic = descriptor.repair !== 'MANUAL';
            return (
              <Card key={group.kind}>
                <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div className="flex flex-col gap-1">
                    <CardTitle className="flex flex-wrap items-center gap-2">
                      {descriptor.title}
                      <Badge
                        tone={descriptor.severity === 'ERROR' ? 'danger' : 'warn'}
                        icon={<AlertTriangle aria-hidden="true" />}
                      >
                        {descriptor.severity === 'ERROR' ? 'Erro' : 'Aviso'}
                      </Badge>
                      <Badge tone="neutral">
                        {group.items.length} {group.items.length === 1 ? 'registro' : 'registros'}
                      </Badge>
                    </CardTitle>
                    <CardDescription>{descriptor.explanation}</CardDescription>
                  </div>
                  {canRepair && automatic && (
                    <Button
                      variant="secondary"
                      className="shrink-0"
                      onClick={() => setPendingRepair(group.items)}
                    >
                      <Wrench aria-hidden="true" />
                      {descriptor.repairLabel}
                    </Button>
                  )}
                </CardHeader>
                <CardContent className="flex flex-col gap-2">
                  {!automatic && (
                    <p className="text-sm text-(--color-state-warn)">
                      Este problema não é corrigido automaticamente: qualquer conserto teria de
                      escolher um valor no lugar de quem cadastrou. Ajuste os registros abaixo pela
                      tela da estrutura.
                    </p>
                  )}
                  <ul className="flex flex-col gap-1 text-sm">
                    {group.items.slice(0, MAX_LISTED).map((item) => (
                      <li
                        key={item.entityType + item.entityId}
                        className="flex flex-wrap gap-x-2 border-b border-(--color-border-subtle) pb-1 last:border-0"
                      >
                        <code className="text-(--color-content-muted)">{item.entityId}</code>
                        <span>{item.detail}</span>
                      </li>
                    ))}
                  </ul>
                  {group.items.length > MAX_LISTED && (
                    <p className="text-sm text-(--color-content-muted)">
                      e mais {group.items.length - MAX_LISTED} registros do mesmo tipo.
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </>
      )}

      <ConfirmDialog
        open={pendingRepair !== undefined}
        onOpenChange={(open) => {
          if (!open) setPendingRepair(undefined);
        }}
        title="Corrigir os registros inconsistentes?"
        description={
          'Serão corrigidos ' +
          String(pendingRepair?.length ?? 0) +
          ' registros. A correção é uma exclusão lógica: o registro sai da operação e ' +
          'permanece na auditoria.'
        }
        confirmLabel="Corrigir"
        destructive
        onConfirm={confirmRepair}
      />
    </div>
  );
}

/** Quantos registros a tela lista por tipo antes de resumir — o resto vira contagem. */
const MAX_LISTED = 20;
